// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module EventExplorerPage
 * @package @geenius/analytics/solidjs-css
 * @description Page-level SolidJS CSS composition for filtering, inspecting,
 * and exporting analytics events.
 */

import type { AnalyticsEvent, DeviceInfo, EventType } from "@geenius/analytics";
import { buildAnalyticsSeoMeta } from "@geenius/analytics";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
} from "@geenius/ui/solidjs-css";
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { EventTable } from "../components/EventTable";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { FadeIn } from "../lib/FadeIn";

const EVENT_TYPE_TRANSLATION_KEYS: Readonly<Record<EventType, string>> = {
  page_view: "analytics.events.types.pageView",
  click: "analytics.events.types.click",
  form_submit: "analytics.events.types.formSubmit",
  custom: "analytics.events.types.custom",
};

export interface EventExplorerFilters {
  types?: EventType[];
  startTime?: number;
  endTime?: number;
  url?: string;
  userId?: string;
  sessionId?: string;
  device?: DeviceInfo["device"];
  search?: string;
}

export type AnalyticsSeoMeta = ReturnType<typeof buildAnalyticsSeoMeta>;

export interface EventExplorerPageProps {
  events?: AnalyticsEvent[];
  initialFilters?: EventExplorerFilters;
  onFiltersChange?: (filters: EventExplorerFilters) => void;
  onExport?: (events: AnalyticsEvent[]) => void;
  isLoading?: boolean;
  maxRows?: number;
  renderSeoMeta?: (meta: AnalyticsSeoMeta) => unknown;
  class?: string;
}

const AnalyticsSeoHead: Component<{ meta: unknown }> = (props) => {
  void props.meta;
  return null;
};

function toDateInputValue(timestamp?: number): string {
  if (!timestamp) return "";
  return new Date(timestamp).toISOString().slice(0, 10);
}

function fromDateInputValue(
  value: string,
  boundary: "start" | "end",
): number | undefined {
  if (!value) return undefined;
  const date = new Date(
    `${value}T${boundary === "start" ? "00:00:00" : "23:59:59"}`,
  );
  return Number.isNaN(date.getTime()) ? undefined : date.getTime();
}

function includesText(value: string | undefined, query: string): boolean {
  return value?.toLowerCase().includes(query) ?? false;
}

function eventMatchesFilters(
  event: AnalyticsEvent,
  filters: EventExplorerFilters,
): boolean {
  if (filters.types?.length && !filters.types.includes(event.type)) {
    return false;
  }
  if (filters.startTime && event.timestamp < filters.startTime) return false;
  if (filters.endTime && event.timestamp > filters.endTime) return false;
  if (filters.device && event.device.device !== filters.device) return false;

  const urlQuery = filters.url?.trim().toLowerCase();
  if (urlQuery && !includesText(event.url, urlQuery)) return false;

  const userQuery = filters.userId?.trim().toLowerCase();
  if (userQuery && !includesText(event.userId, userQuery)) return false;

  const sessionQuery = filters.sessionId?.trim().toLowerCase();
  if (sessionQuery && !includesText(event.sessionId, sessionQuery)) {
    return false;
  }

  const searchQuery = filters.search?.trim().toLowerCase();
  if (searchQuery) {
    const searchable = [
      event.name,
      event.type,
      event.url,
      event.userId,
      event.sessionId,
      event.device.browser,
      event.device.os,
      JSON.stringify(event.properties ?? {}),
    ].join(" ");

    if (!searchable.toLowerCase().includes(searchQuery)) return false;
  }

  return true;
}

export const EventExplorerPage: Component<EventExplorerPageProps> = (props) => {
  const copy = createAnalyticsCopy();
  const [filters, setFilters] = createSignal<EventExplorerFilters>(
    props.initialFilters ?? {},
  );
  const [selectedEventId, setSelectedEventId] = createSignal("");
  const seoMeta = buildAnalyticsSeoMeta({
    title: copy.t("analytics.seo.eventExplorerTitle"),
    description: copy.t("analytics.seo.eventExplorerDescription"),
    path: "/analytics/events",
    keywords: ["analytics", "events", "filters", "export"],
  });
  const filteredEvents = createMemo(() =>
    (props.events ?? []).filter((event) =>
      eventMatchesFilters(event, filters()),
    ),
  );
  const inspectedEvent = createMemo(
    () =>
      filteredEvents().find((event) => event.id === selectedEventId()) ??
      filteredEvents()[0] ??
      null,
  );
  const updateFilters = (patch: Partial<EventExplorerFilters>): void => {
    const next = { ...filters(), ...patch };
    setFilters(next);
    props.onFiltersChange?.(next);
  };

  return (
    <>
      {props.renderSeoMeta ? (
        props.renderSeoMeta(seoMeta)
      ) : (
        <AnalyticsSeoHead meta={seoMeta} />
      )}
      <div
        class={cx(
          "gn-analytics-page",
          "gn-analytics-event-explorer",
          props.class,
        )}
        data-analytics-page="event-explorer"
      >
        <FadeIn>
          <header class="gn-analytics-page__header" data-analytics-page-header>
            <h1 class="gn-analytics-page__title">
              {copy.t("analytics.events.explorerTitle")}
            </h1>
            <p class="gn-analytics-page__subtitle">
              {copy.t("analytics.events.explorerSubtitle")}
            </p>
          </header>
        </FadeIn>

        <FadeIn delay={60}>
          <Card data-analytics-event-filters>
            <CardHeader>
              <CardTitle data-analytics-section-title>
                {copy.t("analytics.events.filters.title")}
              </CardTitle>
            </CardHeader>
            <CardContent class="gn-analytics-event-explorer__filters">
              <label class="gn-analytics-event-explorer__filter">
                <span>{copy.t("analytics.events.filters.search")}</span>
                <input
                  class="gn-analytics-event-explorer__input"
                  value={filters().search ?? ""}
                  onInput={(event) =>
                    updateFilters({ search: event.currentTarget.value })
                  }
                  placeholder={copy.t(
                    "analytics.events.filters.explorerSearchPlaceholder",
                  )}
                />
              </label>
              <label class="gn-analytics-event-explorer__filter">
                <span>{copy.t("analytics.events.filters.type")}</span>
                <select
                  class="gn-analytics-event-explorer__select"
                  value={filters().types?.[0] ?? ""}
                  onChange={(event) =>
                    updateFilters({
                      types: event.currentTarget.value
                        ? [event.currentTarget.value as EventType]
                        : undefined,
                    })
                  }
                >
                  <option value="">
                    {copy.t("analytics.events.filters.allTypes")}
                  </option>
                  <For
                    each={
                      [
                        "page_view",
                        "click",
                        "form_submit",
                        "custom",
                      ] as EventType[]
                    }
                  >
                    {(type) => (
                      <option value={type}>
                        {copy.t(EVENT_TYPE_TRANSLATION_KEYS[type])}
                      </option>
                    )}
                  </For>
                </select>
              </label>
              <label class="gn-analytics-event-explorer__filter">
                <span>{copy.t("analytics.events.filters.url")}</span>
                <input
                  class="gn-analytics-event-explorer__input"
                  value={filters().url ?? ""}
                  onInput={(event) =>
                    updateFilters({ url: event.currentTarget.value })
                  }
                  placeholder={copy.t(
                    "analytics.events.filters.urlPlaceholder",
                  )}
                />
              </label>
              <label class="gn-analytics-event-explorer__filter">
                <span>{copy.t("analytics.events.filters.user")}</span>
                <input
                  class="gn-analytics-event-explorer__input"
                  value={filters().userId ?? ""}
                  onInput={(event) =>
                    updateFilters({ userId: event.currentTarget.value })
                  }
                  placeholder={copy.t(
                    "analytics.events.filters.userPlaceholder",
                  )}
                />
              </label>
              <label class="gn-analytics-event-explorer__filter">
                <span>{copy.t("analytics.events.filters.session")}</span>
                <input
                  class="gn-analytics-event-explorer__input"
                  value={filters().sessionId ?? ""}
                  onInput={(event) =>
                    updateFilters({ sessionId: event.currentTarget.value })
                  }
                  placeholder={copy.t(
                    "analytics.events.filters.sessionPlaceholder",
                  )}
                />
              </label>
              <label class="gn-analytics-event-explorer__filter">
                <span>{copy.t("analytics.events.filters.device")}</span>
                <select
                  class="gn-analytics-event-explorer__select"
                  value={filters().device ?? ""}
                  onChange={(event) =>
                    updateFilters({
                      device:
                        event.currentTarget.value === ""
                          ? undefined
                          : (event.currentTarget.value as DeviceInfo["device"]),
                    })
                  }
                >
                  <option value="">
                    {copy.t("analytics.events.filters.allDevices")}
                  </option>
                  <option value="desktop">{copy.deviceLabel("desktop")}</option>
                  <option value="tablet">{copy.deviceLabel("tablet")}</option>
                  <option value="mobile">{copy.deviceLabel("mobile")}</option>
                </select>
              </label>
              <label class="gn-analytics-event-explorer__filter">
                <span>{copy.t("analytics.events.filters.from")}</span>
                <input
                  class="gn-analytics-event-explorer__input"
                  type="date"
                  value={toDateInputValue(filters().startTime)}
                  onInput={(event) =>
                    updateFilters({
                      startTime: fromDateInputValue(
                        event.currentTarget.value,
                        "start",
                      ),
                    })
                  }
                />
              </label>
              <label class="gn-analytics-event-explorer__filter">
                <span>{copy.t("analytics.events.filters.to")}</span>
                <input
                  class="gn-analytics-event-explorer__input"
                  type="date"
                  value={toDateInputValue(filters().endTime)}
                  onInput={(event) =>
                    updateFilters({
                      endTime: fromDateInputValue(
                        event.currentTarget.value,
                        "end",
                      ),
                    })
                  }
                />
              </label>
            </CardContent>
          </Card>
        </FadeIn>

        <div class="gn-analytics-event-explorer__grid">
          <FadeIn delay={120}>
            <Card data-analytics-event-results>
              <CardHeader>
                <CardTitle data-analytics-section-title>
                  {copy.plural(
                    "analytics.events.resultCount",
                    filteredEvents().length,
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent class="gn-analytics-event-explorer__results">
                <Show
                  when={filteredEvents().length > 0 || props.isLoading}
                  fallback={
                    <EmptyState
                      title={copy.t("analytics.events.noFilterMatches")}
                    />
                  }
                >
                  <EventTable
                    events={filteredEvents()}
                    isLoading={props.isLoading}
                    maxRows={props.maxRows ?? 25}
                    showFilters={false}
                  />
                  <div class="gn-analytics-event-explorer__actions">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={filteredEvents().length === 0}
                      onClick={() => props.onExport?.(filteredEvents())}
                    >
                      {copy.t("analytics.actions.exportCurrentView")}
                    </Button>
                  </div>
                </Show>
              </CardContent>
            </Card>
          </FadeIn>

          <FadeIn delay={180}>
            <Card data-analytics-event-inspector>
              <CardHeader>
                <CardTitle data-analytics-section-title>
                  {copy.t("analytics.events.inspector.shortTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent class="gn-analytics-event-explorer__inspector">
                <Show
                  when={inspectedEvent()}
                  fallback={
                    <EmptyState
                      title={copy.t("analytics.events.inspector.selectEvent")}
                    />
                  }
                >
                  {(event) => (
                    <>
                      <label class="gn-analytics-event-explorer__filter">
                        <span>
                          {copy.t("analytics.events.inspector.selectedEvent")}
                        </span>
                        <select
                          class="gn-analytics-event-explorer__select"
                          value={event().id}
                          onChange={(inputEvent) =>
                            setSelectedEventId(inputEvent.currentTarget.value)
                          }
                        >
                          <For each={filteredEvents()}>
                            {(row) => (
                              <option value={row.id}>
                                {row.name} - {row.id}
                              </option>
                            )}
                          </For>
                        </select>
                      </label>
                      <dl class="gn-analytics-event-explorer__details">
                        <div>
                          <dt>{copy.t("analytics.events.inspector.name")}</dt>
                          <dd>{event().name}</dd>
                        </div>
                        <div>
                          <dt>{copy.t("analytics.events.filters.type")}</dt>
                          <dd>
                            {copy.t(EVENT_TYPE_TRANSLATION_KEYS[event().type])}
                          </dd>
                        </div>
                        <div>
                          <dt>{copy.t("analytics.events.filters.url")}</dt>
                          <dd>{event().url}</dd>
                        </div>
                        <div>
                          <dt>{copy.t("analytics.events.filters.user")}</dt>
                          <dd>
                            {event().userId ??
                              copy.t("analytics.events.inspector.anonymous")}
                          </dd>
                        </div>
                      </dl>
                      <pre class="gn-analytics-event-explorer__properties">
                        {JSON.stringify(event().properties ?? {}, null, 2)}
                      </pre>
                    </>
                  )}
                </Show>
              </CardContent>
            </Card>
          </FadeIn>
        </div>
      </div>
    </>
  );
};
